import React from 'react';
import { Link } from 'react-router-dom';
import NumberBadge from './NumberBadge';
import { Phone, CheckCircle, Info } from 'lucide-react';
import { useListNumbersQuery, useUltramsgStatusQuery } from '@/client/api/whatsapp/list-numbers.query';

const PhoneNumbersPage: React.FC = () => {
  const { data, isLoading } = useListNumbersQuery();
  const numbers = data?.data || [];
  const hasLinked = numbers.some((n) => n.transport === 'ultramsg');
  const { data: linked, isError: linkedError } = useUltramsgStatusQuery(hasLinked);
  const session = linked?.data?.status?.accountStatus;
  const sessionLabel = linkedError
    ? 'status unavailable'
    : session?.status
      ? [session.status, session.substatus].filter(Boolean).join(' / ')
      : 'checking...';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Phone Numbers</h1>
          <p className="text-sm text-gray-500 mt-1">WhatsApp Business phone numbers configured on the server</p>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                Phone numbers are configured via the <code className="font-mono">WHATSAPP_PHONE_NUMBERS</code> (or
                {' '}<code className="font-mono">WHATSAPP_PHONE_NUMBER_ID</code>) environment variable on the server —
                there's no add/remove UI here since numbers come from your Meta Business account, not this database.
              </p>
            </div>
          </div>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Loading...</p>}

        {!isLoading && numbers.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No phone numbers configured</h3>
            <p className="text-sm text-gray-500">Set WHATSAPP_PHONE_NUMBER_ID (or WHATSAPP_PHONE_NUMBERS) on the server</p>
          </div>
        )}

        <div className="space-y-4">
          {numbers.map((num) => (
            <div key={num.phone_number_id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-base font-semibold text-gray-900">
                      {num.display_phone_number || num.alias || 'Default number'}
                    </h3>
                    <NumberBadge id={num.phone_number_id} showNumber={false} />
                    {num.default && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                        <CheckCircle className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                  {num.transport === 'evolution' ? (
                    <p className="text-sm text-gray-600">
                      {num.method || 'Evolution (linked phone)'}
                      {num.label ? ` · ${num.label}` : ''}. Linked and managed on{' '}
                      <Link to="/admin/communications/whatsapp/lines" className="text-blue-600 hover:underline">
                        WhatsApp lines
                      </Link>
                      .
                    </p>
                  ) : num.transport === 'ultramsg' ? (
                    <p className="text-sm text-gray-600">
                      {num.method || 'UltraMsg (linked phone)'}. Session: <span className="font-mono">{sessionLabel}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      {num.method || 'WhatsApp Business (Meta)'}
                      {num.verified_name ? ` as ${num.verified_name}` : ''}
                      {num.alias ? ` · alias ${num.alias}` : ''} · ID <span className="font-mono">{num.phone_number_id}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhoneNumbersPage;
